import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
// Handles the return trip from https://auth.emergentagent.com/. Emergent appends
//   #session_id=<id>
// to the redirect URL. We POST it to /api/auth/emergent-session (server calls
// Emergent's /session-data to verify + fetch the Google profile), store the JWT
// they issue back, and hop to the right dashboard.
export default function AuthCallback() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [msg, setMsg] = useState("Finishing sign-in…");
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      toast.error("No Google session was returned. Please try again.");
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/emergent-session", { session_id: sessionId });
        // Clean the fragment so a page refresh doesn't retry the (single-use) session_id
        window.history.replaceState(null, "", window.location.pathname);
        localStorage.setItem("hn_token", data.token);
        localStorage.setItem("hn_user", JSON.stringify(data.user));
        setUser?.(data.user);
        toast.success(data.created ? `Welcome to TheHostelNow, ${data.user.name.split(" ")[0]}!` : `Welcome back, ${data.user.name.split(" ")[0]}!`);
        const u = data.user;
        const dest = u.role === "admin" ? "/admin" : u.role === "owner" ? "/owner" : "/dashboard";
        nav(dest, { replace: true });
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || "Google sign-in failed";
        toast.error(detail);
        setMsg(`Sign-in failed: ${detail}`);
        setTimeout(() => nav("/login", { replace: true }), 1800);
      }
    })();
  }, [nav, setUser]);

  return (
    <div
      data-testid="auth-callback"
      className="min-h-screen bg-[#FBF5EA] flex items-center justify-center p-6"
    >
      <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center max-w-md w-full shadow-sm">
        <Loader2 size={32} className="mx-auto animate-spin text-orange-800" />
        <div className="mt-4 font-display font-bold text-xl">{msg}</div>
        <div className="mt-2 text-sm text-slate-500">Verifying your Google account securely…</div>
      </div>
    </div>
  );
}
